import { useEffect, useRef, useState } from 'react';
import EditorJS from '@editorjs/editorjs';
import type { OutputData } from '@editorjs/editorjs';
import List from '@editorjs/list';
import Underline from '@editorjs/underline';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { ChevronDownIcon, ChevronUpIcon } from 'lucide-react';
import type { ListCounterType } from '@worldathletics/types';

import { BlockMenu, BlockReferencedIndicator } from '@/components/atoms';
import { HtmlContent } from '@/components/atoms/html-content/html-content';
import type { RootState, AppDispatch } from '@/context/store/store';
import {
  moveNode,
  updateNode,
  type DocumentEntity,
} from '@/context/store/slices/documentDataSlice';
import {
  setAddBlockIntent,
  setNewBlockModal,
  setDeleteBlockConfirmationModal,
} from '@/context/store/slices/siteSlice';
import { ColorTool, ReferenceTool } from '@/editorjs-tools';
import { useReferenceMap } from '@/context/references/references-context';
import { useReferenceRowLookup } from '@/context/references/reference-row-index-context';
import {
  useOpenEditLink,
  useEditLinkActiveSourceId,
} from '@/context/edit-link/edit-link-context';
import {
  canonicalizeStoredText,
  normalizeInlineHtml,
} from '@/lib/editorjs-save-adapter';
import { chipHtmlToTokens } from '@/lib/edit-text-serialize';
import { renderTextToHtml } from '@/lib/edit-text-serialize';
import { reconcileListChips } from '@/lib/reconcile-list-chips';
import { resolveEditLinkOpenFromListRange } from '@/lib/resolve-edit-link-open';
import { sanitizeBlockHtml } from '@/lib/sanitize';
import { useSiblingPosition } from '@/hooks/use-sibling-position';
import { useReportActiveBlock } from '@/hooks/use-report-active-block';
import { registerEditorFlush, setEditorFlushDirty } from '@/lib/editor-flush-registry';
import { useViewportEditorActivation } from '@/hooks/use-viewport-editor-activation';
import { useEditorInteractionPins } from '@/hooks/use-editor-interaction-pins';

import './document-editor-list.scss';

type Props = { node: DocumentEntity & { type: 'list' } };

type ListItemData = { content: string; meta: Record<string, unknown>; items: [] };

type ListBlockData = {
  style: 'ordered' | 'unordered';
  meta?: { start?: number; counterType?: string };
  items: ListItemData[];
};

const DEBOUNCE_MS = 300;

// Mirrors `document-view-list.tsx`'s mapping — used for the static
// equivalent rendered while this block is viewport-deactivated.
const COUNTER_TYPE_TO_CSS: Record<ListCounterType, string> = {
  numeric: 'decimal',
  'lower-alpha': 'lower-alpha',
  'upper-alpha': 'upper-alpha',
  'lower-roman': 'lower-roman',
  'upper-roman': 'upper-roman',
};

function mapEditorOutputToChanges(output: OutputData): {
  style: 'ordered' | 'unordered';
  items: string[];
  meta?: Props['node']['meta'];
} | null {
  const block = output.blocks[0];
  if (!block || block.type !== 'list') return null;
  const data = block.data as ListBlockData;
  const items = (data.items ?? []).map(item =>
    normalizeInlineHtml(chipHtmlToTokens(item.content ?? '')),
  );
  return {
    style: data.style ?? 'unordered',
    items,
    ...(data.meta && Object.keys(data.meta).length > 0
      ? { meta: data.meta as Props['node']['meta'] }
      : {}),
  };
}

function listChangesMatch(
  prev: Props['node'],
  changes: NonNullable<ReturnType<typeof mapEditorOutputToChanges>>,
): boolean {
  const sameStyle = prev.style === changes.style;
  const sameItems =
    prev.items.length === changes.items.length &&
    prev.items.every(
      (item, i) =>
        canonicalizeStoredText(item) ===
        canonicalizeStoredText(changes.items[i] ?? ''),
    );
  const sameMeta =
    JSON.stringify(prev.meta ?? {}) === JSON.stringify(changes.meta ?? {});
  return sameStyle && sameItems && sameMeta;
}

async function saveListEditorToRedux(
  editor: EditorJS,
  dispatch: AppDispatch,
  getNode: () => Props['node'],
): Promise<void> {
  const output = await editor.save();
  const changes = mapEditorOutputToChanges(output);
  if (!changes) return;
  const prev = getNode();
  if (listChangesMatch(prev, changes)) return;
  dispatch(
    updateNode({
      id: prev.id,
      changes,
    }),
  );
}

function DocumentEditorList({ node }: Props) {
  const dispatch = useDispatch();
  const store = useStore<RootState>();
  const bookId = useSelector(
    (state: RootState) => state.documentData.sourceNodeKey?.split(':')[0],
  );
  const nodeId = useSelector(
    (state: RootState) => state.documentData.sourceNodeKey?.split(':')[1],
  );
  const refMap = useReferenceMap();
  const openEditLink = useOpenEditLink();
  const { getReferences } = useReferenceRowLookup();
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const holderRef = useRef<HTMLDivElement | null>(null);
  const editorRef = useRef<EditorJS | null>(null);
  const isReadyRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const flushListEditorSaveRef = useRef<() => Promise<void>>(async () => {});
  const nodeRef = useRef(node);
  const refMapRef = useRef(refMap);
  const position = useSiblingPosition(node.id);
  const reportActive = useReportActiveBlock(node);
  const [isMenuOpen, setIsMenuOpen] = useState(false);

  // Viewport-gated activation (Phase 5): keeps far-offscreen list blocks
  // from each carrying a live EditorJS instance on very large documents.
  const { shouldMountEditor, pin, unpin } =
    useViewportEditorActivation(wrapperRef);
  useEditorInteractionPins(wrapperRef, { pin, unpin });
  const shouldMountEditorRef = useRef(shouldMountEditor);
  useEffect(() => {
    shouldMountEditorRef.current = shouldMountEditor;
  });

  // Pin while our own block menu is open.
  useEffect(() => {
    if (isMenuOpen) {
      pin('menu');
    } else {
      unpin('menu');
    }
  }, [isMenuOpen, pin, unpin]);

  // Pin while this list block is the active EditLink source — list
  // references use the whole list block's own id as the source
  // (`resolveEditorOwnerId` treats list blocks as their own owner).
  const activeEditLinkSourceId = useEditLinkActiveSourceId();
  useEffect(() => {
    if (activeEditLinkSourceId === node.id) {
      pin('modal');
    } else {
      unpin('modal');
    }
  }, [activeEditLinkSourceId, node.id, pin, unpin]);

  // `null` → EditorJS is (or should be) mounted. Non-null → deactivated:
  // holds the height measured just before destroy so the static
  // equivalent doesn't collapse the layout while offscreen.
  const [staticSnapshot, setStaticSnapshot] = useState<{
    minHeight: number;
  } | null>(() => (shouldMountEditor ? null : { minHeight: 0 }));
  // Bumped on every reactivation (static → editor) so the EditorJS
  // creation effect re-runs and constructs a fresh instance from the
  // CURRENT `node` prop — never a stale mount-time snapshot.
  const [mountEpoch, setMountEpoch] = useState(0);
  const deactivationInFlightRef = useRef(false);
  // Reserves the last on-screen height across the static → editor swap on
  // reactivation. Construction is deferred (see the `setTimeout` in the
  // creation effect below), so without this the holder briefly renders
  // empty at `min-height: 0` — collapsing this block for a frame and
  // shifting everything below it. Cleared once EditorJS reports `onReady`.
  const [reservedHeight, setReservedHeight] = useState<number | null>(null);

  useEffect(() => {
    nodeRef.current = node;
  }, [node]);

  useEffect(() => {
    refMapRef.current = refMap;
  }, [refMap]);

  // Repaints chip labels/data-broken/data-lost in place when refMap resolves
  // after this block already mounted (e.g. references query still loading
  // on a cold page load) — the init effect below only paints chips once,
  // from whatever refMap existed at EditorJS init time.
  useEffect(() => {
    const root = holderRef.current;
    if (!root) return;
    const itemEls = [...root.querySelectorAll<HTMLElement>('.cdx-list__item')];
    reconcileListChips(itemEls, node.items, refMap, node.id);
  }, [node.items, refMap]);

  useEffect(() => {
    if (!holderRef.current || editorRef.current) return;
    // Viewport-gated activation: skip constructing EditorJS entirely while
    // offscreen and unpinned. Re-runs (via `mountEpoch`) once
    // `shouldMountEditor` flips back to true — see the reactivation effect
    // below.
    if (!shouldMountEditorRef.current) return;

    let cancelled = false;
    let editor: EditorJS | null = null;
    isReadyRef.current = false;

    const initTimer = setTimeout(() => {
      if (cancelled || !holderRef.current) return;
      holderRef.current.replaceChildren();

      const initBlock = {
        id: nodeRef.current.id,
        type: 'list' as const,
        data: {
          style: nodeRef.current.style,
          meta: nodeRef.current.meta ?? {},
          items: nodeRef.current.items.map(content => ({
            content: renderTextToHtml(
              content,
              refMapRef.current,
              nodeRef.current.id,
            ),
            meta: {},
            items: [] as [],
          })),
        },
      };

      editor = new EditorJS({
        holder: holderRef.current,
        minHeight: 0,
        tools: {
          list: {
            class: List as never,
            inlineToolbar: [
              'bold',
              'italic',
              'underline',
              'reference',
              'color',
            ],
            config: { defaultStyle: nodeRef.current.style, maxLevel: 1 },
          },
          underline: { class: Underline as never },
          reference: {
            class: ReferenceTool as never,
            config: {
              onPickerOpen: (range: Range) => {
                void flushListEditorSaveRef.current().then(() => {
                  const docNodeState = store.getState().documentNode.data;
                  // Read lazily at picker-open time — no per-list-block
                  // TanStack Query observer, and no dependency on the
                  // query's data identity in the effect below (which used
                  // to force-remount this EditorJS instance on every
                  // references refetch across the whole page).
                  const refs = getReferences() ?? [];
                  const entity = store.getState().documentData.byId[
                    nodeRef.current.id
                  ];
                  const items =
                    entity &&
                    'items' in entity &&
                    Array.isArray(entity.items)
                      ? entity.items
                      : nodeRef.current.items;
                  const result = resolveEditLinkOpenFromListRange(range, {
                    items,
                    sourceBlockId: nodeRef.current.id,
                    refs: refs as unknown as Parameters<
                      typeof resolveEditLinkOpenFromListRange
                    >[1]['refs'],
                    meta: {
                      nodeId: docNodeState.id,
                      bookId: docNodeState.bookId,
                      language: docNodeState.language,
                    },
                  });
                  if (result) openEditLink(result.sourceRef, result.mode);
                });
              },
            },
          },
          color: { class: ColorTool as never },
        },
        data: { time: Date.now(), version: '2.30', blocks: [initBlock] },
        onReady: () => {
          isReadyRef.current = true;
          const ceBlock =
            holderRef.current?.querySelector<HTMLElement>('.ce-block');
          ceBlock?.setAttribute('data-doc-id', nodeRef.current.id);
          // Let the real content's layout settle for one frame before
          // dropping the reserved-height spacer (see `reservedHeight`) —
          // avoids a visible collapse-then-grow between the spacer height
          // and the actual rendered height.
          requestAnimationFrame(() => {
            if (!cancelled) setReservedHeight(null);
          });
        },
        onChange: () => {
          setEditorFlushDirty(nodeRef.current.id, true);
          if (debounceRef.current) clearTimeout(debounceRef.current);
          debounceRef.current = setTimeout(() => {
            const e = editorRef.current;
            if (!e || typeof e.save !== 'function') return;
            void saveListEditorToRedux(e, dispatch, () => nodeRef.current).then(
              () => {
                setEditorFlushDirty(nodeRef.current.id, false);
              },
            );
          }, DEBOUNCE_MS);
        },
      });

      editorRef.current = editor;
      flushListEditorSaveRef.current = async () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
          debounceRef.current = null;
        }
        const e = editorRef.current;
        if (!e || typeof e.save !== 'function') return;
        await saveListEditorToRedux(e, dispatch, () => nodeRef.current);
        setEditorFlushDirty(nodeRef.current.id, false);
      };
    }, 0);

    const unregisterFlush = registerEditorFlush(node.id, {
      flush: async () => {
        await flushListEditorSaveRef.current();
      },
      cancel: () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
          debounceRef.current = null;
        }
        setEditorFlushDirty(node.id, false);
      },
      isBusy: () => debounceRef.current !== null,
    });

    return () => {
      const wasReady = isReadyRef.current;
      cancelled = true;
      isReadyRef.current = false;
      clearTimeout(initTimer);
      unregisterFlush();
      if (debounceRef.current) clearTimeout(debounceRef.current);
      if (editorRef.current?.destroy) {
        void editorRef.current.destroy();
        editorRef.current = null;
      }
      // If this instance was constructed but torn down before `onReady`
      // ever fired, nothing else will clear `reservedHeight` — drop it here
      // instead of leaving a stale spacer. A never-constructed instance
      // (still in the deferred `setTimeout`, or cancelled before it fired)
      // deliberately leaves any existing reservation alone — clearing it
      // here would reopen the exact collapse this state exists to prevent.
      if (editor && !wasReady) {
        setReservedHeight(null);
      }
    };
    // Remount (fresh holder DOM node) after external mutations is driven by
    // the parent's epoch-scoped React key (document-editor-node.tsx), not by
    // an internal effect dependency — avoids destroying/recreating an
    // EditorJS instance in place on a shared holder node (async destroy()
    // racing a same-node re-init), which the cluster's key-based remount
    // already avoids by construction.
    //
    // Deliberately NOT depending on the references query's data — `getReferences`
    // is a stable getter (see `ReferenceRowIndexProvider`) read lazily inside
    // the `onPickerOpen` closure above, so a references refetch elsewhere on
    // the page no longer destroys/recreates every list block's EditorJS
    // instance.
  }, [dispatch, node.id, openEditLink, mountEpoch]);

  // Reactivation: once `shouldMountEditor` flips back to true while we're
  // showing the static equivalent, drop the snapshot and bump `mountEpoch`
  // so the creation effect above re-runs — it always reads `nodeRef.current`
  // (kept in sync by the effect right above), so reactivation initializes
  // from live Redux state, never a stale mount-time snapshot.
  useEffect(() => {
    if (shouldMountEditor) {
      if (staticSnapshot !== null) {
        // Measure the CURRENTLY on-screen static content — not the height
        // captured back when this block deactivated — since the text or
        // viewport width may have changed while it was offscreen. Held
        // until EditorJS reports `onReady` so the swap never collapses this
        // block's height, which is what otherwise shifts the scroll
        // position onto the next sibling.
        const measured = wrapperRef.current?.getBoundingClientRect().height;
        if (measured) setReservedHeight(measured);
        setStaticSnapshot(null);
        setMountEpoch(v => v + 1);
      }
      return;
    }

    // Going offscreen and unpinned: deactivate, but only if an editor is
    // actually mounted right now.
    if (staticSnapshot !== null) return;
    if (deactivationInFlightRef.current) return;
    if (!editorRef.current) return;

    deactivationInFlightRef.current = true;
    void (async () => {
      try {
        // 1. Pins (focus/menu/selection/modal) are already reflected in
        //    `shouldMountEditor` itself — reaching this branch means none
        //    are held. 2. Await this owner's pending/in-flight save so a
        //    debounced edit from just before scrolling away never gets
        //    dropped.
        await flushListEditorSaveRef.current();

        // Re-check after the async flush: a pin may have been taken, or
        // the block may have scrolled back into view, while we were
        // awaiting the save.
        if (shouldMountEditorRef.current) return;

        // 3. Measure the rendered height before destroying so the static
        // equivalent can hold that height and avoid a layout jump.
        const measuredHeight =
          holderRef.current?.getBoundingClientRect().height ?? 0;

        // 4. Destroy EditorJS.
        const editor = editorRef.current;
        editorRef.current = null;
        if (editor && typeof editor.destroy === 'function') {
          try {
            editor.destroy();
          } catch {
            // EditorJS sometimes throws on destroy if the holder is already gone.
          }
        }

        // 5. Render the static equivalent at the retained height.
        setStaticSnapshot({ minHeight: measuredHeight });
      } finally {
        deactivationInFlightRef.current = false;
      }
    })();
  }, [shouldMountEditor, staticSnapshot]);

  function handleMoveUp(): void {
    if (!position.canMoveUp) return;
    dispatch(
      moveNode({
        id: node.id,
        newParentId: position.parentId,
        newIndex: position.siblingIndex - 1,
      }),
    );
  }

  function handleMoveDown(): void {
    if (!position.canMoveDown) return;
    dispatch(
      moveNode({
        id: node.id,
        newParentId: position.parentId,
        newIndex: position.siblingIndex + 1,
      }),
    );
  }

  const ListTag = node.style === 'ordered' ? 'ol' : 'ul';
  const listStart = node.meta?.start;
  const listCounterType = node.meta?.counterType;

  return (
    <div
      ref={wrapperRef}
      data-type="card"
      className={`doc-editor-list is-${node.style} ${isMenuOpen ? 'is-menu-open' : ''}`}
      onFocusCapture={reportActive}
    >
      <button
        type="button"
        className="doc-editor-list__move-btn is-up"
        onClick={handleMoveUp}
        disabled={!position.canMoveUp}
        aria-label="Move up"
      >
        <ChevronUpIcon size={16} />
      </button>
      <button
        type="button"
        className="doc-editor-list__move-btn is-down"
        onClick={handleMoveDown}
        disabled={!position.canMoveDown}
        aria-label="Move down"
      >
        <ChevronDownIcon size={16} />
      </button>
      <div className="doc-editor-list__toolbar">
        <BlockReferencedIndicator blockId={node.id} />
        <BlockMenu
          variant="leaf"
          showNumbering={false}
          onDelete={() => {
            dispatch(
              setDeleteBlockConfirmationModal({
                open: true,
                data: {
                  bookId: bookId ?? '',
                  nodeId: nodeId ?? '',
                  blockId: node.id,
                  blockType: 'list',
                },
              }),
            );
          }}
          onAddAbove={() => {
            dispatch(
              setAddBlockIntent({ mode: 'sibling-before', targetId: node.id }),
            );
            dispatch(setNewBlockModal(true));
          }}
          onAddBelow={() => {
            dispatch(
              setAddBlockIntent({ mode: 'sibling-after', targetId: node.id }),
            );
            dispatch(setNewBlockModal(true));
          }}
          onMoveUp={handleMoveUp}
          onMoveDown={handleMoveDown}
          canMoveUp={position.canMoveUp}
          canMoveDown={position.canMoveDown}
          onOpenChange={setIsMenuOpen}
        />
      </div>
      {staticSnapshot ? (
        <div
          className="doc-editor-list__static"
          style={
            staticSnapshot.minHeight > 0
              ? { minHeight: staticSnapshot.minHeight }
              : undefined
          }
        >
          <ListTag
            className="doc-editor-list__static-list"
            {...(node.style === 'ordered' && listStart !== undefined
              ? { start: listStart }
              : {})}
            style={
              node.style === 'ordered' && listCounterType
                ? { listStyleType: COUNTER_TYPE_TO_CSS[listCounterType] }
                : undefined
            }
          >
            {node.items.map((item, index) => (
              <li key={index} className="doc-editor-list__static-item">
                <HtmlContent
                  as="span"
                  html={sanitizeBlockHtml(
                    renderTextToHtml(item, refMap, node.id),
                  )}
                />
              </li>
            ))}
          </ListTag>
        </div>
      ) : (
        <div
          ref={holderRef}
          className="doc-editor-list__holder"
          style={
            reservedHeight != null ? { minHeight: reservedHeight } : undefined
          }
        />
      )}
    </div>
  );
}

export { DocumentEditorList };
